import React, { useEffect, useRef } from 'react';
import { X, Keyboard } from 'lucide-react';
import { useModalEscape } from '../hooks/useModalEscape';

/**
 * [PURPOSE]: Keyboard shortcuts help modal
 * [WHY]: Users need to discover available shortcuts
 * [INTEGRATION]: Triggered by pressing ? key
 * [ACCESSIBILITY]: Focus trap keeps keyboard navigation within modal
 */

const KeyboardShortcutsHelp = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // [APR 2026]: Escape dismisses the shortcuts help — was missing before.
  useModalEscape(isOpen, onClose);

  // [ACCESSIBILITY]: Focus trap - keep Tab navigation within modal
  useEffect(() => {
    if (!isOpen) return;

    // Focus the close button when modal opens
    closeButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // If Shift+Tab on first element, go to last
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
        // If Tab on last element, go to first
        else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const shortcuts = [
    { key: 'H', description: 'Heal 1 HP', category: 'Combat' },
    { key: 'D', description: 'Take 1 damage', category: 'Combat' },
    { key: 'L', description: 'Open level-up wizard', category: 'Character' },
    { key: 'E', description: 'Toggle equipment panel', category: 'Navigation' },
    { key: 'S', description: 'Toggle spells panel', category: 'Navigation' },
    { key: 'I', description: 'Focus inventory search', category: 'Navigation' },
    { key: 'R', description: 'Open rest panel', category: 'Character' },
    { key: 'J', description: 'Open journal', category: 'Navigation' },
    { key: '?', description: 'Show this help', category: 'Help' },
    { key: 'Esc', description: 'Close modals/panels', category: 'Navigation' },
  ];

  const categories = ['Combat', 'Character', 'Navigation', 'Help'];

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[999] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-title"
    >
      <div
        ref={modalRef}
        className="bg-gray-800 rounded-lg border-2 border-purple-500 p-6 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 id="shortcuts-title" className="text-2xl font-bold text-white flex items-center gap-3">
            <Keyboard size={28} className="text-purple-400" />
            Keyboard Shortcuts
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400 rounded"
            aria-label="Close keyboard shortcuts help"
          >
            <X size={24} />
          </button>
        </div>

        {/* Shortcuts by category */}
        {categories.map(category => {
          const categoryShortcuts = shortcuts.filter(s => s.category === category);
          if (categoryShortcuts.length === 0) return null;

          return (
            <div key={category} className="mb-6 last:mb-0">
              <h3 className="text-sm font-bold text-purple-300 uppercase tracking-wider mb-3">
                {category}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3 hover:bg-gray-900/70 transition-colors"
                  >
                    <span className="text-gray-300">{shortcut.description}</span>
                    <kbd className="px-3 py-1 bg-gray-700 text-white rounded border border-gray-600 font-mono text-sm font-bold min-w-[3rem] text-center">
                      {shortcut.key}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Footer note */}
        <div className="mt-6 pt-6 border-t border-gray-700">
          <p className="text-sm text-gray-400 text-center">
            💡 Tip: Shortcuts work globally when not typing in an input field
          </p>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
